// DayZ-style inventory grid/stack model — the whole data model, with NO
// Babylon and no bundler-only imports, so tools/invtest.mjs can unit-test it
// in plain Node. src/game/items.js is a `.js` under src/ (CommonJS from
// Node's point of view: package.json has no "type":"module"), so the catalog
// is INJECTED here instead of imported — see the module-system note in
// docs/OVERHAUL.md. src/game/inventory.js is the thin bundle-facing shim that
// binds the real ITEMS to this class.
//
// Equipped clothing/backpack items each carry a grid; items occupy w×h cells
// (no rotation yet). Quickbar size comes from clothing.

const SLOTS = ['shirt', 'jacket', 'pants', 'backpack'];
// placement order when something is added: coat pockets before trouser
// pockets before the pack, so the pack keeps its big contiguous space free
const FILL_ORDER = ['jacket', 'shirt', 'pants', 'backpack'];

function slotFor(def) {
  if (def.kind === 'backpack') return 'backpack';
  return def.kind === 'shirt' || def.kind === 'jacket' || def.kind === 'pants' ? def.kind : null;
}

function cellsOf(def) {
  return def.grid ? def.grid[0] * def.grid[1] : 0;
}

function clampCond(v) {
  // `cond` is 0..1 and absent means factory-fresh (ui/inventory.js:75). A
  // restored blob is untrusted input, so an out-of-range or non-numeric value
  // becomes "no condition recorded" rather than a NaN that paints a broken bar.
  if (typeof v !== 'number' || !Number.isFinite(v)) return undefined;
  return Math.max(0, Math.min(1, v));
}

// The spill list from equip() used to be a single {id,qty} object, and for one
// round the first stack's `.id`/`.qty` were mirrored onto the returned array so
// an un-updated caller would still drop SOMETHING. That mirror is gone: it made
// the bug it was covering for invisible — main.js read `.id` off the array,
// dropped stack 1 of 22 and destroyed the other 21, and it compiled, ran and
// looked like a successful drop. Reading `.id` off a list must now be a visible
// `undefined` rather than a silent tail loss.
//
// null (not an empty array) still means "nothing spilled": acquire() below
// tests the dry run with `if (this._clone().equip(id))`, and an empty array is
// truthy.
function spillList(list) {
  return list.length ? list : null;
}

export class InventoryCore {
  constructor(items) {
    this.ITEMS = items;
    this.equipment = { shirt: null, jacket: null, pants: null, backpack: null };
    this.quickbar = [null, null, null, null]; // uid or null; visible count = quickslotCount()
    this.onChange = null;
    // uids are per-inventory so a dry-run clone cannot collide with the real
    // one and nothing outside depends on them being globally unique
    this._nextUid = 1;
    // restore() bookkeeping: how many saved records had to be moved, and how
    // many could not be saved at all. Initialised here so a caller can read
    // them even when restore() refused the blob outright.
    this.restoreFixups = 0;
    this.restoreLost = 0;
  }

  quickslotCount() {
    let n = 0;
    for (const key of ['shirt', 'jacket', 'pants']) {
      const eq = this.equipment[key];
      if (eq) n += this.ITEMS[eq.id].quickslots || 0;
    }
    return Math.min(4, n);
  }

  containers() {
    const out = [];
    for (const key of FILL_ORDER) {
      const eq = this.equipment[key];
      if (eq) {
        const def = this.ITEMS[eq.id];
        out.push({ slot: key, label: def.name, eq, grid: def.grid, items: eq.items });
      }
    }
    return out;
  }

  fits(cont, gw, gh, x, y, w, h, ignore) {
    if (x < 0 || y < 0 || x + w > gw || y + h > gh) return false;
    for (const it of cont) {
      if (it === ignore) continue;
      const d = this.ITEMS[it.id];
      if (x < it.x + d.size[0] && it.x < x + w && y < it.y + d.size[1] && it.y < y + h) return false;
    }
    return true;
  }

  findSpot(cont, gw, gh, w, h) {
    for (let y = 0; y <= gh - h; y++) {
      for (let x = 0; x <= gw - w; x++) {
        if (this.fits(cont, gw, gh, x, y, w, h, null)) return [x, y];
      }
    }
    return null;
  }

  // ---- two-phase add (A2) -------------------------------------------------
  // Phase one: work out EVERY mutation an add would make without touching a
  // single stack. The old addItem wrote as it went and returned false halfway
  // through, and loot.pick / forage.pick / animals.butcher / loot.searchCorpse
  // all read false as "nothing was taken" and left the world item in place —
  // free duplication on every near-full pickup. Returns null when the whole
  // quantity does not fit, which is the only honest answer those callers can
  // act on.
  _planAdd(id, qty) {
    const def = this.ITEMS[id];
    if (!def) return null;
    let left = Math.floor(Number(qty));
    if (!(left > 0)) return null;
    const stack = Math.max(1, def.stack || 1);
    const tops = [];   // {it, add} — top up an existing stack
    const puts = [];   // {items, x, y, qty} — a new stack in a free cell block
    if (stack > 1) {
      for (const c of this.containers()) {
        for (const it of c.items) {
          if (left <= 0) break;
          if (it.id !== id || it.qty >= stack) continue;
          const take = Math.min(left, stack - it.qty);
          tops.push({ it, add: take });
          left -= take;
        }
        if (left <= 0) break;
      }
    }
    // Virtual occupancy per container: a planned placement must block the next
    // one, or two stacks get planned into the same cells and the second write
    // silently overlaps the first.
    const virt = new Map();
    while (left > 0) {
      let placed = false;
      for (const c of this.containers()) {
        if (!virt.has(c.items)) virt.set(c.items, c.items.slice());
        const cells = virt.get(c.items);
        const spot = this.findSpot(cells, c.grid[0], c.grid[1], def.size[0], def.size[1]);
        if (!spot) continue;
        const put = Math.min(left, stack);
        cells.push({ id, x: spot[0], y: spot[1] });   // reserve it for the plan
        puts.push({ items: c.items, x: spot[0], y: spot[1], qty: put });
        left -= put;
        placed = true;
        break;
      }
      if (!placed) return null;   // no room for the rest: the add does not happen
    }
    return { tops, puts };
  }

  // dry run: would addItem(id, qty) store the WHOLE quantity?
  canFit(id, qty = 1) {
    return !!this._planAdd(id, qty);
  }

  // add item id (stacking onto existing stacks first); all-or-nothing, and the
  // return value is the truth about whether anything was stored. `cond` is
  // carried onto NEW stacks only — equip() uses it so taking a worn jacket off
  // does not hand it back factory-fresh (which would be free item laundering).
  addItem(id, qty = 1, cond = undefined) {
    const plan = this._planAdd(id, qty);
    if (!plan) return false;
    for (const t of plan.tops) t.it.qty += t.add;
    const c = clampCond(cond);
    for (const p of plan.puts) {
      const rec = { uid: this._nextUid++, id, x: p.x, y: p.y, qty: p.qty };
      if (c !== undefined) rec.cond = c;
      p.items.push(rec);
    }
    this.onChange?.();
    return true;
  }

  findByUid(uid) {
    for (const c of this.containers()) {
      const it = c.items.find((i) => i.uid === uid);
      if (it) return { cont: c, it };
    }
    return null;
  }

  // a quickslot pointing at a uid that no longer exists paints an empty slot
  // that still swallows the keypress — drop those refs whenever items move
  _pruneQuickbar() {
    for (let i = 0; i < this.quickbar.length; i++) {
      const uid = this.quickbar[i];
      if (uid != null && !this.findByUid(uid)) this.quickbar[i] = null;
    }
  }

  removeItem(it, qty = 1) {
    const found = this.findByUid(it.uid);
    if (!found) return;
    it.qty -= qty;
    if (it.qty <= 0) {
      const i = found.cont.items.indexOf(it);
      if (i >= 0) found.cont.items.splice(i, 1);
      const qi = this.quickbar.indexOf(it.uid);
      if (qi >= 0) this.quickbar[qi] = null;
    }
    this.onChange?.();
  }

  move(it, toCont, x, y) {
    const from = this.findByUid(it.uid);
    const def = this.ITEMS[it.id];
    const sameCont = from && from.cont.eq === toCont.eq;
    if (!this.fits(toCont.items, toCont.grid[0], toCont.grid[1], x, y, def.size[0], def.size[1], sameCont ? it : null)) return false;
    if (from && !sameCont) {
      const i = from.cont.items.indexOf(it);
      if (i >= 0) from.cont.items.splice(i, 1);
    }
    if (!sameCont) toCont.items.push(it);
    it.x = x; it.y = y;
    this.onChange?.();
    return true;
  }

  // equip clothing/backpack from inventory (or thin air for starter gear).
  // The old piece and everything in it is unequipped INTO the inventory; every
  // stack that finds no room is returned so the caller can drop it on the
  // ground (A1). The previous version recorded only the FIRST failure and then
  // `spilled || {...}` threw the rest away: a 3×3 jacket swapped for a 2×2 took
  // 9 carried units down to 6 and silently deleted painkillers, antibiotics
  // and matches.
  equip(idOrIt) {
    const it = typeof idOrIt === 'string' ? { uid: this._nextUid++, id: idOrIt, qty: 1 } : idOrIt;
    if (!it || !this.ITEMS[it.id]) return null;
    const def = this.ITEMS[it.id];
    const slot = slotFor(def);
    if (!slot) return null;
    const found = this.findByUid(it.uid);
    if (found) {
      const i = found.cont.items.indexOf(it);
      if (i >= 0) found.cont.items.splice(i, 1);
    }
    const old = this.equipment[slot];
    // Identity is the UID, not the id. Re-equipping the very same record (a
    // cond update, say) must keep its contents — but a SECOND flannel shirt
    // out of your pack is a different physical garment, and comparing ids
    // meant that spare was silently deleted on equip.
    const sameItem = !!old && old.uid !== undefined && old.uid === it.uid;
    // Two garments of the same id share a grid, so the contents simply move
    // across with the slot instead of being re-homed one stack at a time.
    const sameKind = !!old && old.id === it.id;
    this.equipment[slot] = {
      id: it.id, uid: it.uid, cond: clampCond(it.cond),
      items: sameKind ? old.items : [],
    };
    const spilled = [];
    if (old && !sameItem) {
      // the garment itself first — it is the biggest single thing to re-home,
      // and re-homing it before its contents is what the player expects
      if (!this.addItem(old.id, 1, old.cond)) spilled.push({ id: old.id, qty: 1, cond: old.cond });
      if (!sameKind) {
        for (const inner of old.items) {
          if (!this.addItem(inner.id, inner.qty, inner.cond)) {
            spilled.push({ id: inner.id, qty: inner.qty, cond: inner.cond });
          }
        }
        // every stack is now either carried or in `spilled` — never both, and
        // never still listed inside a container nobody is wearing
        old.items.length = 0;
      }
    }
    this._pruneQuickbar();
    this.onChange?.();
    return spillList(spilled);
  }

  // Pick-up path for loot/forage/corpses: a garment or pack that fits in no
  // pocket is still a real find (backpack_hiking is 4×4 and only an already
  // large pack has a 4×4 hole, so without this it was unobtainable — B6).
  // Wearing it is offered only as a strict upgrade, and only when the swap
  // spills NOTHING, so a pickup can never cost you what you were carrying.
  // Returns {ok, equipped}.
  acquire(id, qty = 1) {
    if (this.addItem(id, qty)) return { ok: true, equipped: false };
    const def = this.ITEMS[id];
    if (!def || Math.floor(Number(qty)) !== 1) return { ok: false, equipped: false };
    const slot = slotFor(def);
    if (!slot) return { ok: false, equipped: false };
    const old = this.equipment[slot];
    if (old && cellsOf(def) <= cellsOf(this.ITEMS[old.id])) return { ok: false, equipped: false };
    // simulate on a copy: equip() is deterministic for a given state, so a
    // clean dry run guarantees the real swap spills nothing either
    if (this._clone().equip(id)) return { ok: false, equipped: false };
    this.equip(id);
    return { ok: true, equipped: true };
  }

  // structural copy of the model alone (no onChange hook) for dry runs; items
  // are flat records, so a shallow copy per item is exact
  _clone() {
    const c = new InventoryCore(this.ITEMS);
    c._nextUid = this._nextUid;
    for (const slot of SLOTS) {
      const e = this.equipment[slot];
      c.equipment[slot] = e ? { id: e.id, uid: e.uid, cond: e.cond, items: e.items.map((i) => ({ ...i })) } : null;
    }
    c.quickbar = this.quickbar.slice();
    return c;
  }

  totalOf(id) {
    let n = 0;
    for (const c of this.containers()) for (const it of c.items) if (it.id === id) n += it.qty;
    return n;
  }

  // every unit carried, equipped garments included — what an integrity test
  // compares across an operation that must not create or destroy property
  census() {
    const out = new Map();
    const bump = (id, n) => out.set(id, (out.get(id) || 0) + n);
    for (const slot of SLOTS) {
      const e = this.equipment[slot];
      if (!e) continue;
      bump(e.id, 1);
      for (const it of e.items) bump(it.id, it.qty);
    }
    return out;
  }

  // ---- server persistence (multiplayer saves) ----
  toJSON() {
    const eq = {};
    for (const slot of SLOTS) {
      const e = this.equipment[slot];
      eq[slot] = e
        ? { id: e.id, cond: e.cond, items: e.items.map((i) => ({ id: i.id, x: i.x, y: i.y, qty: i.qty, cond: i.cond })) }
        : null;
    }
    // uids are regenerated on every restore, so a saved quickbar has to name
    // WHERE the item is, not which uid it had. slot + grid cell survives a
    // reconnect; a uid never could, which is why every quickslot assignment
    // was lost on reconnect (A4).
    const quickbar = this.quickbar.map((uid) => {
      if (uid == null) return null;
      const f = this.findByUid(uid);
      return f ? { slot: f.cont.slot, x: f.it.x, y: f.it.y } : null;
    });
    return { equipment: eq, quickbar };
  }

  // A FAILED VALIDATION MAY NEVER DELETE PROPERTY. Every rejection below used
  // to be a bare `continue` — out of grid, overlapping an accepted item, a
  // garment in the wrong slot — and main.js writes the post-restore inventory
  // back to the server 20 s later on its save interval, so a deletion here is
  // committed to world.json before the player can notice, let alone report it.
  // Nobody is harmed by today's file (audited read-only over all 70 saved
  // inventories: 0 garments rejected, 0 stacks dropped, unit counts identical),
  // but the trigger is on the agenda: the day any
  // `size` or `grid` in items.js changes, every saved item that no longer fits
  // would be deleted for every player holding one.
  //
  // So a record that cannot keep its saved COORDINATES is re-homed instead:
  // first to another spot in the same container, then — once every slot is
  // known — through addItem() into whatever pocket has room. Only a record
  // that fits nowhere at all is lost, and `restoreFixups` / `restoreLost`
  // count both so a caller can tell the player.
  restore(data) {
    if (!data?.equipment) return false;
    this.restoreFixups = 0;   // records that moved to a different cell/pocket
    this.restoreLost = 0;     // records nothing could re-home (unknown id, no room)
    const carry = [];         // {id, qty, cond} waiting for a pocket
    for (const slot of SLOTS) {
      const e = data.equipment[slot];
      const def = e && this.ITEMS[e.id];
      // a blob is untrusted: the id must exist AND belong in this slot, or a
      // saved "pants" holding a backpack grid writes a container nothing can
      // render and nothing can take items out of. The slot is still cleared —
      // but its CONTENTS (and the garment itself, when the catalog knows it)
      // go into `carry` rather than over the side.
      if (!def || slotFor(def) !== slot) {
        if (e) {
          if (def) carry.push({ id: e.id, qty: 1, cond: clampCond(e.cond) });
          for (const raw of e.items || []) {
            const d = raw && this.ITEMS[raw.id];
            if (!d) { if (raw) this.restoreLost++; continue; }
            // same clamp the in-grid path applies: a blob claiming qty 9999 of
            // a stack-1 item must not make addItem plan 9999 placements
            const stack = Math.max(1, d.stack || 1);
            carry.push({
              id: raw.id,
              qty: Math.min(stack, Math.max(1, Math.floor(Number(raw.qty) || 1))),
              cond: clampCond(raw.cond),
            });
          }
        }
        this.equipment[slot] = null;
        continue;
      }
      const items = [];
      for (const raw of e.items || []) {
        const d = raw && this.ITEMS[raw.id];
        // an id the catalog no longer has is the one case nothing can save: it
        // has no size, no icon and no name to put in a message
        if (!d) { if (raw) this.restoreLost++; continue; }
        const stack = Math.max(1, d.stack || 1);
        const qty = Math.min(stack, Math.max(1, Math.floor(Number(raw.qty) || 1)));
        let x = Math.floor(Number(raw.x));
        let y = Math.floor(Number(raw.y));
        // inside the grid AND not on top of an item we already accepted:
        // neither was checked before, so a save written under a bigger garment
        // (or a hand-edited world.json) put items outside the grid and stacked
        // them on each other, where they are invisible and unreachable
        if (!Number.isFinite(x) || !Number.isFinite(y)
          || !this.fits(items, def.grid[0], def.grid[1], x, y, d.size[0], d.size[1], null)) {
          const spot = this.findSpot(items, def.grid[0], def.grid[1], d.size[0], d.size[1]);
          if (!spot) { carry.push({ id: raw.id, qty, cond: clampCond(raw.cond) }); continue; }
          [x, y] = spot;
          this.restoreFixups++;
        }
        items.push({ uid: this._nextUid++, id: raw.id, x, y, qty, cond: clampCond(raw.cond) });
      }
      this.equipment[slot] = { id: e.id, uid: this._nextUid++, cond: clampCond(e.cond), items };
    }
    // Now that every slot is known, re-home whatever had nowhere to go in its
    // own container. addItem() is all-or-nothing per stack, so a stack that
    // still does not fit is genuinely beyond saving — and rec.prev on the
    // server (mp-server.mjs onSave) is the hand-recovery path for that.
    for (const c of carry) {
      if (this.addItem(c.id, c.qty, c.cond)) this.restoreFixups++;
      else this.restoreLost += c.qty;
    }
    this.quickbar = [null, null, null, null];
    for (let i = 0; i < 4; i++) {
      const q = data.quickbar?.[i];
      if (!q || typeof q !== 'object') continue;
      const cont = this.containers().find((c) => c.slot === q.slot);
      const it = cont?.items.find((o) => o.x === Math.floor(Number(q.x)) && o.y === Math.floor(Number(q.y)));
      if (it) this.quickbar[i] = it.uid;
    }
    this._pruneQuickbar();
    this.onChange?.();
    return true;
  }
}

export { SLOTS, slotFor, cellsOf, clampCond };
